import { useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';

/**
 * One measuring helper to replace the ad-hoc position math each menu/popover
 * used to do on its own (docs/plans/panel-system.md, "Shared components to
 * build"). Web only — native has no floating popovers, so the hook returns
 * null there and callers render nothing.
 */

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

// Kept small on purpose (panel-system.md: "keep the set small"): a
// horizontal pair (right/left) and two vertical pairs (bottom/top, each with
// a start/end alignment), which covers every anchored menu in the app today
// (the create menu goes right-start, ContentActionMenu goes bottom-end).
export type Placement =
  | 'right-start'
  | 'left-start'
  | 'bottom-start'
  | 'top-start'
  | 'bottom-end'
  | 'top-end';

export interface AnchoredPosition {
  top: number;
  left: number;
  placement: Placement;
}

const VIEWPORT_MARGIN = 8;

const OPPOSITE: Record<Placement, Placement> = {
  'right-start': 'left-start',
  'left-start': 'right-start',
  'bottom-start': 'top-start',
  'top-start': 'bottom-start',
  'bottom-end': 'top-end',
  'top-end': 'bottom-end',
};

function placeAt(
  anchor: Rect,
  size: Size,
  placement: Placement,
  gap: number
): { top: number; left: number } {
  switch (placement) {
    case 'right-start':
      return { top: anchor.top, left: anchor.left + anchor.width + gap };
    case 'left-start':
      return { top: anchor.top, left: anchor.left - size.width - gap };
    case 'bottom-start':
      return { top: anchor.top + anchor.height + gap, left: anchor.left };
    case 'top-start':
      return { top: anchor.top - size.height - gap, left: anchor.left };
    case 'bottom-end':
      return {
        top: anchor.top + anchor.height + gap,
        left: anchor.left + anchor.width - size.width,
      };
    case 'top-end':
      return {
        top: anchor.top - size.height - gap,
        left: anchor.left + anchor.width - size.width,
      };
  }
}

// A placement's "side" decides which axis can overflow: right/left placements
// only ever get squeezed horizontally (they're already top-aligned to the
// anchor), bottom/top placements only vertically.
function overflowsViewport(
  pos: { top: number; left: number },
  size: Size,
  viewport: Size,
  placement: Placement
): boolean {
  if (placement === 'right-start' || placement === 'left-start') {
    return pos.left < VIEWPORT_MARGIN || pos.left + size.width > viewport.width - VIEWPORT_MARGIN;
  }
  return pos.top < VIEWPORT_MARGIN || pos.top + size.height > viewport.height - VIEWPORT_MARGIN;
}

/**
 * Pure placement math: where the popover lands for a given anchor rect,
 * popover size, viewport and preferred placement — flipping to the opposite
 * side when the preferred one would overflow, then clamping to the viewport
 * with an 8px margin either way. No DOM access, so it's exhaustively unit
 * tested on its own (see __tests__/hooks/useAnchoredPosition.test.ts).
 */
export function computeAnchoredPosition(
  anchorRect: Rect,
  popoverSize: Size,
  viewport: Size,
  placement: Placement,
  gap = 8
): AnchoredPosition {
  let finalPlacement = placement;
  let pos = placeAt(anchorRect, popoverSize, placement, gap);

  if (overflowsViewport(pos, popoverSize, viewport, placement)) {
    finalPlacement = OPPOSITE[placement];
    pos = placeAt(anchorRect, popoverSize, finalPlacement, gap);
  }

  const maxLeft = Math.max(viewport.width - popoverSize.width - VIEWPORT_MARGIN, VIEWPORT_MARGIN);
  const maxTop = Math.max(viewport.height - popoverSize.height - VIEWPORT_MARGIN, VIEWPORT_MARGIN);

  return {
    top: Math.min(Math.max(pos.top, VIEWPORT_MARGIN), maxTop),
    left: Math.min(Math.max(pos.left, VIEWPORT_MARGIN), maxLeft),
    placement: finalPlacement,
  };
}

/**
 * Tracks the anchored position for a popover anchored to `anchorRef`,
 * recomputing on resize and scroll (capture phase, so a scroll inside any
 * ancestor container triggers it too). Returns null on native, before the
 * anchor/size are measurable, or once the caller sets `popoverSize` to null
 * (its own signal that the popover isn't open).
 */
export function useAnchoredPosition(
  anchorRef: React.RefObject<unknown>,
  popoverSize: Size | null,
  placement: Placement,
  gap = 8
): AnchoredPosition | null {
  const [position, setPosition] = useState<AnchoredPosition | null>(null);

  const recompute = useCallback(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !popoverSize) {
      setPosition(null);
      return;
    }
    const node = anchorRef.current as unknown as { getBoundingClientRect?: () => Rect } | null;
    if (typeof node?.getBoundingClientRect !== 'function') {
      setPosition(null);
      return;
    }
    const anchorRect = node.getBoundingClientRect();
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    setPosition(computeAnchoredPosition(anchorRect, popoverSize, viewport, placement, gap));
  }, [anchorRef, popoverSize, placement, gap]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [recompute]);

  return Platform.OS === 'web' ? position : null;
}
